import i18n from "i18next";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { faLink, faUnlink } from "@fortawesome/pro-regular-svg-icons";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { ProjectQueryApi } from "src/api/generated/erp/project/project/api/projectQueryApi.ts";
import { CustomerOrderQueryApi } from "src/api/generated/erp/sales/customerOrder/api/customerOrderQueryApi.ts";
import { PurchaseOrderProjectsApi } from "src/api/generated/erp/purchase/purchaseOrder/api/purchaseOrderProjectsApi.ts";
import { PurchaseOrderCustomerOrdersApi } from "src/api/generated/erp/purchase/purchaseOrder/api/purchaseOrderCustomerOrdersApi.ts";
import {
	getPurchaseOrderLinkedOrdersApi_PurchaseOrderLinkedOrderTypeLabels,
	PurchaseOrderLinkedOrdersApi,
	PurchaseOrderLinkedOrdersApi_PurchaseOrderLinkedOrderDto,
} from "src/api/generated/erp/purchase/purchaseOrder/api/purchaseOrderLinkedOrdersApi.ts";
import { ProjectTreeViewContainer } from "src/components/views/erp/project/treeView/ProjectTreeViewContainer.tsx";
import { CustomerOrdersPage } from "src/components/views/erp/sales/customerOrder/CustomerOrdersPage.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { OpenProjectInTreeViewButton } from "../../utilComponents/OpenProjectInTreeViewButton.tsx";
import { OpenCustomerOrderButton } from "../../utilComponents/OpenCustomerOrderButton.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface PurchaseOrderLinkedOrdersDataGridProps {
	purchaseOrderId: number;
	siteId: number;
	orderSource: string;
}

export const PurchaseOrderLinkedOrdersDataGrid = ({
	purchaseOrderId,
	siteId,
	orderSource,
}: PurchaseOrderLinkedOrdersDataGridProps) => {
	const showConfirmDialog = useConfirmDialog();
	const showInputDialog = useInputDialog();
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();

	return (
		<ClientSideDataGridModel
			gridId={"E3F1E1EFD0B6417B"}
			fetchData={() => PurchaseOrderLinkedOrdersApi.getLinkedOrdersForPurchaseOrder({ purchaseOrderId })}
			getRows={(data) => data}
			getRowId={(row) => `${row.type}_${row.linkId}`}
			initialParams={{}}
			selectFirstRowOnLoad
			render={({ dataGridProps, onlySelectedRow, refreshData }) => (
				<AavoDataGrid<PurchaseOrderLinkedOrdersApi_PurchaseOrderLinkedOrderDto>
					columns={[
						enumColumn({
							field: "type",
							headerName: i18n.t("type"),
							width: 160,
							enumLabels: getPurchaseOrderLinkedOrdersApi_PurchaseOrderLinkedOrderTypeLabels(),
						}),
						integerColumn({
							field: "id",
							headerName: i18n.t("number_shortened"),
							width: 120,
						}),
						textColumn({
							field: "description",
							headerName: i18n.t("description"),
							width: 320,
							flex: 1,
						}),
					]}
					actionBarComponents={
						<>
							{hasPermission(Permissions.Projects.READ) && orderSource === "MANUAL" && (
								<AsyncButton
									label={i18n.t("connect_to_project")}
									icon={faLink}
									variant={"outlined"}
									onClick={async () => {
										const projectId = await showInputDialog({
											defaultValue: null,
											type: "singleNumberSelect",
											title: i18n.t("connect_to_project"),
											fieldLabel: i18n.t("project"),
											required: true,
											fieldProps: {
												selection: {
													options: async ({ searchQuery, currentSelection }) => {
														const projects =
															await ProjectQueryApi.getProjectSelectionOptions({
																searchQuery,
																currentSelection,
																siteId,
																includeTemplates: false,
															});
														return projects.map((project) => ({
															value: project.projectId,
															label: project.projectDescription,
														}));
													},
												},
											},
										});
										if (projectId == null) return;
										await PurchaseOrderProjectsApi.connectPurchaseOrderToProject({
											purchaseOrderId,
											projectId,
										});
										await refreshData();
									}}
								/>
							)}
							{hasPermission(Permissions.Sales.CustomerOrders.READ) && orderSource === "MANUAL" && (
								<AsyncButton
									label={i18n.t("connect_to_customer_order")}
									icon={faLink}
									variant={"outlined"}
									onClick={async () => {
										const customerOrderId = await showInputDialog({
											defaultValue: null,
											type: "singleNumberSelect",
											title: i18n.t("connect_to_customer_order"),
											fieldLabel: i18n.t("customer_order"),
											required: true,
											fieldProps: {
												selection: {
													options: async ({ searchQuery, currentSelection }) => {
														const customerOrders =
															await CustomerOrderQueryApi.getCustomerOrderOptions({
																searchQuery,
																currentSelection,
																siteId,
															});
														return customerOrders.map((order) => ({
															value: order.customerOrderId,
															label: `${order.customerOrderId} | ${order.orderReference}`,
														}));
													},
												},
											},
										});
										if (customerOrderId == null) return;
										await PurchaseOrderCustomerOrdersApi.connectPurchaseOrderToCustomerOrder({
											purchaseOrderId,
											customerOrderId,
										});
										await refreshData();
									}}
								/>
							)}
							<AsyncButton
								label={i18n.t("unlink")}
								icon={faUnlink}
								variant={"outlined"}
								disabled={!onlySelectedRow}
								onClick={async () => {
									if (!onlySelectedRow) return;
									const confirmed = await showConfirmDialog();
									if (!confirmed) return;

									if (onlySelectedRow.type === "PROJECT") {
										await PurchaseOrderProjectsApi.disconnectPurchaseOrderFromProject({
											purchaseOrderProjectId: onlySelectedRow.linkId,
										});
									} else {
										await PurchaseOrderCustomerOrdersApi.disconnectPurchaseOrderFromCustomerOrder({
											purchaseOrderCustomerOrderId: onlySelectedRow.linkId,
										});
									}
									await refreshData();
								}}
							/>
						</>
					}
					rowContextMenuComponents={({ row }) => [
						row.type === "CUSTOMER_ORDER" && (
							<OpenCustomerOrderButton key={"openCustomerOrder"} customerOrderId={row.id} />
						),
						row.type === "PROJECT" && (
							<OpenProjectInTreeViewButton key={"openProject"} projectId={row.id} />
						),
					]}
					onRowDoubleClick={(params) => {
						if (params.row.type === "PROJECT") {
							openDialog({
								title: i18n.t("project"),
								content: <ProjectTreeViewContainer onlyProjectId={params.row.id} />,
								agentContext: {
									key: defineAgentUiContextKey("erp.project.project.purchaseOrderLinkedOrdersDataGrid.dialog"),
									title: i18n.t("project"),
									pathSegments: [params.row.description],
									data: {
										projectId: params.row.id,
										purchaseOrderId: purchaseOrderId,
									},
								},
							});
						} else {
							openDialog({
								title: i18n.t("customer_order"),
								content: <CustomerOrdersPage onlyCustomerOrderToShowId={params.row.id} />,
								agentContext: {
									key: defineAgentUiContextKey("erp.sales.customerOrder.purchaseOrderLinkedOrdersDataGrid.dialog"),
									title: i18n.t("customer_order"),
									pathSegments: [params.row.description],
									data: {
										customerOrderId: params.row.id,
										purchaseOrderId: purchaseOrderId,
									},
								},
							});
						}
					}}
					agentContext={{
						key: defineAgentUiContextKey("erp.purchase.purchaseOrderLinkedOrders.grid"),
						data: {
							purchaseOrderId: purchaseOrderId,
						},
						rowFields: ["type", "id", "linkId"],
						selectedRowPathSegment: (row) => row.description,
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
